import React from 'react';

/**
 * Why a title or episode wouldn't play.
 *
 * Pressing Play searches every indexer, ranks what comes back, and tries the
 * top few. When none of that ends in a stream there is nothing on screen to say
 * where it broke — a dead indexer, only season packs, an episode the packs
 * don't name, every candidate a dead swarm. This is the trace of that one
 * attempt, so "some episodes source and some don't" stops being a mystery.
 *
 * It only renders on failure, and reads entirely off what the attempt already
 * gathered: `meta` from the search (per-indexer health, how many rows were
 * dropped as irrelevant) and `attempts` from playback (each release tried and
 * how it failed). Shared by the film hero and the episode list — a film is just
 * an attempt with no episode label.
 */

const outcomeLabel = {
  failed: (a) => a.reason || 'could not open',
  'needs-pick': () => 'no matching episode inside',
  overtaken: () => 'another release answered first',
  // Not tried, and worth saying so: it was ranked high enough to play but had
  // nobody seeding it, so it was passed over rather than waited on.
  'no-seeders': () => 'no seeders — skipped',
  // In flight. This is the row that sits for the peer-finding timeout, which is
  // most of the time anyone spends waiting on Play.
  trying: () => 'looking for peers…',
  playing: () => 'started'
};

const SourceLog = ({ log }) => {
  if (!log) return null;

  const { label, isEpisode, meta, found, attempts, pending } = log;
  const sources = meta?.sources || [];

  // The server now says *why* rows were dropped, not just how many. The split
  // is the whole point: a title that won't play because every hit was the wrong
  // episode is a different problem from one where the query pulled in the wrong
  // work, and this is where "some episodes source and some don't" gets answered.
  // Tolerate the old scalar shape in case a stale response is in flight.
  const rej = meta?.rejected;
  const rejected = typeof rej === 'number' ? rej : rej?.total || 0;
  const breakdown =
    rej && typeof rej === 'object'
      ? [
          [rej.wrongEpisode, 'wrong episode'],
          [rej.irrelevant, 'off-title'],
          [rej.dubbed, 'dubbed'],
          [rej.adult, 'adult'],
          [rej.cam, 'cam recording'],
          [rej.junk, 'non-video']
        ]
          .filter(([n]) => n)
          .map(([n, kind]) => `${n} ${kind}`)
      : [];

  return (
    <div className="srclog" role="status" aria-busy={pending || undefined}>
      <div className="srclog-head">
        <span className="srclog-title">{pending ? 'Sourcing' : 'Couldn’t source'}</span>
        {label && <span className="srclog-scope">{label}</span>}
      </div>

      {sources.length > 0 && (
        <div className="srclog-sect">
          <div className="srclog-k">Indexers</div>
          <div className="srclog-srcs">
            {sources.map((s) => {
              const state = !s.ok ? 'down' : s.count ? 'ok' : 'empty';
              return (
                <span key={s.source} className={`srclog-src is-${state}`}>
                  <span className="srclog-src-name">{s.source}</span>
                  <span className="srclog-src-v">{s.ok ? s.count : s.error || 'error'}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="srclog-sect">
        <div className="srclog-k">Releases</div>
        <div className="srclog-v">
          {found} matched{rejected ? ` · ${rejected} dropped` : ''}
          {breakdown.length > 0 && (
            <span className="srclog-breakdown"> ({breakdown.join(', ')})</span>
          )}
        </div>
      </div>

      {attempts.length > 0 && (
        <div className="srclog-sect">
          <div className="srclog-k">Tried</div>
          <ol className="srclog-tries">
            {attempts.map((a, i) => (
              <li key={i} className={`srclog-try is-${a.outcome}`}>
                <span className="srclog-try-name">
                  {(a.release.resolution || 'SD').toUpperCase()}
                  <span className="srclog-try-sz">
                    {' · '}
                    {a.release.size}
                    {a.release.isSeasonPack ? ' · pack' : ''}
                  </span>
                </span>
                <span className="srclog-try-out">{(outcomeLabel[a.outcome] || (() => a.outcome))(a)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {found === 0 && (
        <div className="srclog-note">
          No release named this {isEpisode ? 'episode' : 'title'}
          {rejected
            ? ` — ${breakdown.length ? breakdown.join(', ') : `${rejected} dropped`}.`
            : ' on the indexers we can reach.'}
        </div>
      )}
    </div>
  );
};

export default SourceLog;
